import { useCallback, useEffect, useRef, useState } from "react"

interface UseInViewOptions {
  rootMargin?: string
  threshold?: number | number[]
  /** When true, the hook stops observing after the first time the element enters the viewport. */
  once?: boolean
}

/**
 * Tracks whether the referenced element is intersecting the viewport.
 *
 * Uses a callback ref so the observer is (re)attached whenever the target
 * element mounts, unmounts, or changes. Returns `[setRef, inView]`.
 */
export function useInView<T extends Element = Element>({
  rootMargin,
  threshold,
  once = false,
}: UseInViewOptions = {}): [(node: T | null) => void, boolean] {
  const [inView, setInView] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const frozenRef = useRef(false)

  const setRef = useCallback(
    (node: T | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (!node || frozenRef.current) return
      if (typeof IntersectionObserver === "undefined") {
        setInView(true)
        return
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          setInView(entry.isIntersecting)
          if (entry.isIntersecting && once) {
            frozenRef.current = true
            observer.disconnect()
          }
        },
        { rootMargin, threshold }
      )

      observer.observe(node)
      observerRef.current = observer
    },
    [rootMargin, threshold, once]
  )

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return [setRef, inView]
}
